import { create } from 'zustand';
import { captureEvent } from '@/lib/analytics/posthog';

// Helper function to track events in PostHog
const trackEvent = (eventName: string, properties?: Record<string, unknown>): void => {
  captureEvent(eventName, properties);
};

interface GroupBuyState {
  // Search and filters
  searchTerm: string;
  productType: string | undefined;
  status: string | undefined;
  sortBy: string;
  priceRange: { min: number | undefined; max: number | undefined };
  targetQuantityRange: { min: number | undefined; max: number | undefined };
  progressRange: { min: number | undefined; max: number | undefined };
  endingTimeframe: string | undefined;
  endingDate: Date | undefined;

  // Pagination
  currentPage: number;

  // Batch update control
  isUpdating: boolean;

  // Actions
  setSearchTerm: (term: string) => void;
  setProductType: (type: string | undefined) => void;
  setStatus: (status: string | undefined) => void;
  setSortBy: (sort: string) => void;
  setPriceRange: (min?: number, max?: number) => void;
  setTargetQuantityRange: (min?: number, max?: number) => void;
  setProgressRange: (min?: number, max?: number) => void;
  setEndingTimeframe: (timeframe: string | undefined) => void;
  setEndingDate: (date: Date | undefined) => void;
  setCurrentPage: (page: number) => void;
  resetFilters: () => void;
  // New batch update methods
  batchUpdateFilters: (updates: Partial<GroupBuyState>) => void;
  startBatchUpdate: () => void;
  endBatchUpdate: () => void;
}

export const useGroupBuyStore = create<GroupBuyState>((set, get) => ({
  // Initial state
  searchTerm: '',
  productType: undefined,
  status: 'active', // Default to active group buys
  sortBy: 'ending_soonest',
  priceRange: { min: undefined, max: undefined },
  targetQuantityRange: { min: undefined, max: undefined },
  progressRange: { min: undefined, max: undefined },
  endingTimeframe: undefined,
  endingDate: undefined,
  currentPage: 1,
  isUpdating: false,

  // Actions
  setSearchTerm: (term) => {
    trackEvent('groupbuy_search', { term });
    set({ searchTerm: term, currentPage: 1 });
  },

  setProductType: (productType) => {
    if (get().isUpdating) {
      set({ productType });
      return;
    }
    trackEvent('groupbuy_filter_product_type', { productType: productType || 'all' });
    set({ productType, currentPage: 1 });
  },

  setStatus: (status) => {
    if (get().isUpdating) {
      set({ status });
      return;
    }
    trackEvent('groupbuy_filter_status', { status: status || 'all' });
    set({ status, currentPage: 1 });
  },

  setSortBy: (sortBy) => {
    if (get().isUpdating) {
      set({ sortBy });
      return;
    }
    trackEvent('groupbuy_sort', { sortBy });
    set({ sortBy });
  },

  setPriceRange: (min, max) => {
    if (get().isUpdating) {
      set({ priceRange: { min, max } });
      return;
    }
    trackEvent('groupbuy_filter_price', { min, max });
    set({ priceRange: { min, max }, currentPage: 1 });
  },

  setTargetQuantityRange: (min, max) => {
    if (get().isUpdating) {
      set({ targetQuantityRange: { min, max } });
      return;
    }
    trackEvent('groupbuy_filter_target_quantity', { min, max });
    set({ targetQuantityRange: { min, max }, currentPage: 1 });
  },

  setProgressRange: (min, max) => {
    if (get().isUpdating) {
      set({ progressRange: { min, max } });
      return;
    }
    trackEvent('groupbuy_filter_progress', { min, max });
    set({ progressRange: { min, max }, currentPage: 1 });
  },

  setEndingTimeframe: (timeframe) => {
    if (get().isUpdating) {
      set({ endingTimeframe: timeframe, endingDate: undefined });
      return;
    }
    trackEvent('groupbuy_filter_ending_timeframe', { timeframe });
    set({ endingTimeframe: timeframe, endingDate: undefined, currentPage: 1 });
  },

  setEndingDate: (date) => {
    if (get().isUpdating) {
      set({ endingDate: date, endingTimeframe: undefined });
      return;
    }
    trackEvent('groupbuy_filter_ending_date', { date: date?.toISOString() });
    set({ endingDate: date, endingTimeframe: undefined, currentPage: 1 });
  },

  setCurrentPage: (page) => {
    if (get().isUpdating) {
      set({ currentPage: page });
      return;
    }
    trackEvent('groupbuy_pagination', { page });
    set({ currentPage: page });
  },

  resetFilters: () => {
    trackEvent('groupbuy_reset_filters');
    set({
      searchTerm: '',
      productType: undefined,
      status: 'active',
      priceRange: { min: undefined, max: undefined },
      targetQuantityRange: { min: undefined, max: undefined },
      progressRange: { min: undefined, max: undefined },
      endingTimeframe: undefined,
      endingDate: undefined,
      currentPage: 1,
    });
  },

  // New batch update methods
  startBatchUpdate: () => {
    set({ isUpdating: true });
  },

  endBatchUpdate: () => {
    set({ isUpdating: false, currentPage: 1 });
  },

  batchUpdateFilters: (updates) => {
    const { isUpdating } = get();

    // Track the event
    trackEvent('groupbuy_batch_filter_update', updates);

    // If we're not already in a batch update, reset page to 1
    if (!isUpdating) {
      set({ ...updates, currentPage: 1 });
    } else {
      set(updates);
    }
  },
}));
